import React, { useState, useEffect } from 'react';

/**
 * BrandLoader Component
 * 
 * Shows a fleet-branded loading screen during page transitions and data fetching.
 * Features:
 * - Fleet-specific app icon in the center
 * - Auto-hides when loading completes
 * - Prevents flashing on instant loads (minimum display time)
 */
const BrandLoader = ({ isLoading, fleetCode, minDisplayTime = 300 }) => {
  const [showLoader, setShowLoader] = useState(false);
  const [displayStartTime, setDisplayStartTime] = useState(null);
  const [themeColor, setThemeColor] = useState('#1e40af'); // Default blue

  // Determine manifest path based on fleet code with fallback to default.
  // NOTE: Branding assets live under /public/branding/<FLEET>/... (not /manifests/...).
  // index.html and scripts/generate-icons.js use the same /branding/... convention.
  const BRANDING_ROOT = '/branding';
  const DEFAULT_BRAND_PATH = `${BRANDING_ROOT}/default`;
  const DEFAULT_ICON_PATH = `${DEFAULT_BRAND_PATH}/icons/android-chrome-192x192.png`;

  const normalizedCode = fleetCode?.toUpperCase().trim() || null;
  const [manifestPath, setManifestPath] = useState(DEFAULT_BRAND_PATH);
  const [iconPath, setIconPath] = useState(DEFAULT_ICON_PATH);

  // Determine manifest path with fallback logic
  useEffect(() => {
    const determineManifestPath = async () => {
      // If no fleet code, use default
      if (!normalizedCode) {
        setManifestPath(DEFAULT_BRAND_PATH);
        setIconPath(DEFAULT_ICON_PATH);
        return;
      }

      // Check if fleet-specific manifest exists
      const fleetPath = `${BRANDING_ROOT}/${normalizedCode}`;
      try {
        const response = await fetch(`${fleetPath}/manifest.json`, { method: 'HEAD' });
        if (response.ok) {
          setManifestPath(fleetPath);
          setIconPath(`${fleetPath}/icons/android-chrome-192x192.png`);
          return;
        }
      } catch (error) {
        // Manifest doesn't exist, will fall back to default
      }

      // Fall back to default
      setManifestPath(DEFAULT_BRAND_PATH);
      setIconPath(DEFAULT_ICON_PATH);
    };

    determineManifestPath();
  }, [normalizedCode]);

  // Load theme color from manifest
  useEffect(() => {
    fetch(`${manifestPath}/manifest.json`)
      .then(response => {
        if (!response.ok) throw new Error('Manifest not found');
        return response.json();
      })
      .then(manifest => {
        if (manifest.theme_color) {
          setThemeColor(manifest.theme_color);
        }
      })
      .catch(error => {
        console.log('Using default theme color for loader');
        // Keep default theme color
      });
  }, [manifestPath]);

  useEffect(() => {
    if (isLoading) {
      // Start showing loader
      setDisplayStartTime(Date.now());
      setShowLoader(true);
    } else {
      // Hide loader, but ensure minimum display time
      if (displayStartTime !== null) {
        const elapsed = Date.now() - displayStartTime;
        const remaining = Math.max(0, minDisplayTime - elapsed);
        
        if (remaining > 0) {
          // Wait for minimum display time before hiding
          const timer = setTimeout(() => {
            setShowLoader(false);
            setDisplayStartTime(null);
          }, remaining);
          
          return () => clearTimeout(timer);
        } else {
          // Minimum time already elapsed, hide immediately
          setShowLoader(false);
          setDisplayStartTime(null);
        }
      } else {
        setShowLoader(false);
      }
    }
  }, [isLoading, minDisplayTime, displayStartTime]);

  // Don't render if not showing
  if (!showLoader) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'opacity 0.3s ease-in-out',
      }}
    >
      {/* App icon */}
      <div
        className="flex items-center justify-center"
        style={{
          width: '120px',
          height: '120px',
          borderRadius: '12px',
          backgroundColor: 'white',
          boxShadow: `0 4px 12px ${themeColor}30, 0 2px 4px rgba(0, 0, 0, 0.1)`,
          padding: '16px',
        }}
      >
        <img
          src={iconPath}
          alt="App Icon"
          className="w-full h-full object-contain"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
          onError={(e) => {
            // Fallback chain: fleet icon -> default branding icon -> top-level logo192.
            // Guard against infinite loops by checking the current src.
            const current = e.target.getAttribute('src') || '';
            if (!current.endsWith(DEFAULT_ICON_PATH)) {
              e.target.src = DEFAULT_ICON_PATH;
            } else if (!current.endsWith('/logo192.png')) {
              e.target.src = '/logo192.png';
            }
          }}
        />
      </div>
    </div>
  );
};

export default BrandLoader;

